import { clsx } from 'clsx'
import { type FC, type ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type OfferV11,
  PrescriptionV3PrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../global-config'
import { TENANT } from '../../../../tenant'
import { TosSuperscript } from '../../../../tos-superscripts'
import {
  priceAccessorGetThirtyDaysPriceTrend,
  PRODUCT_THIRTY_DAYS_PRICE_TREND,
} from '../../../model/priceAccessor/priceAccessorGetThirtyDaysPriceTrend'
import { productAccessorIsSaeOffer } from '../../../model/productAccessor/productAccessorIsSaeOffer'
import { productAccessorOfferNowProductGroupIsSameDayNextDay } from '../../../model/productAccessor/productAccessorOfferNowProductGroupIsSameDayNextDay'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'
import { useProductPageRebrushStyle } from '../../product-page-context/useProductPageRebrushStyle'
import { useProductPageContext } from '../../productPageContext'
import { ProductPriceTransparencyIcon } from '../ProductPriceTransparencyIcon'
import { useShowProductThirtyDaysPrice } from '../useShowProductThirtyDaysPrice'

const getRetailOrPrescriptionPriceLabel = ({
  currentPrescriptionType,
  isAtSaeNowOffer,
  isOtc,
  superScripts,
}: {
  currentPrescriptionType: string
  isAtSaeNowOffer: boolean
  isOtc: boolean
  superScripts: PublicGlobalConfig['superScripts']
}): ReactElement => {
  const otcOrPrivatePrescriptionMessage = isAtSaeNowOffer ? (
    <FormattedMessage id="productPricing.buyBox.shopApothekePrice" />
  ) : (
    <FormattedMessage id="general.label.yourPrice" />
  )

  return (
    <>
      {isOtc ||
      currentPrescriptionType ===
        PrescriptionV3PrescriptionTypeEnum.PrivatePrescription ? (
        otcOrPrivatePrescriptionMessage
      ) : (
        <FormattedMessage id="general.label.additionalCharge" />
      )}
      <TosSuperscript value={superScripts.price} />
    </>
  )
}

const getIsAtSaeNowOffer = (
  currentOffer: OfferV11 | undefined,
  tenant: TENANT,
): boolean => {
  const isSaeOffer = Boolean(
    currentOffer && productAccessorIsSaeOffer(currentOffer),
  )

  const nowEligible: boolean = currentOffer
    ? productAccessorOfferNowProductGroupIsSameDayNextDay(currentOffer)
    : false

  return tenant === TENANT.AT && isSaeOffer && nowEligible
}

const getIsErxWithRecommendedPrice = (
  currentPrescriptionType: PrescriptionV3PrescriptionTypeEnum,
): boolean => {
  return (
    PrescriptionV3PrescriptionTypeEnum.InsurancePrescription ===
      currentPrescriptionType ||
    PrescriptionV3PrescriptionTypeEnum.FreePrescription ===
      currentPrescriptionType
  )
}

export const ProductPageSingleAttributesHeaderLabels: FC = () => {
  const { superScripts, tenant } = useGlobalConfigContext()
  const { currentOffer, currentPrescriptionType, productTemplate } =
    useProductPageContext()
  const showThirtyDayPrice = useShowProductThirtyDaysPrice(currentOffer)
  const isOtc =
    productTemplate === PRODUCT_TEMPLATES.OTC ||
    currentPrescriptionType ===
      PrescriptionV3PrescriptionTypeEnum.NoPrescription
  const isRebrushStyle = useProductPageRebrushStyle()
  const isErxWithRecommendedPrice: boolean = getIsErxWithRecommendedPrice(
    currentPrescriptionType,
  )
  const thirtyDayPriceTrend = priceAccessorGetThirtyDaysPriceTrend(
    currentOffer,
    currentPrescriptionType,
  )
  const isAtSaeNowOffer = getIsAtSaeNowOffer(currentOffer, tenant)
  const retailOrPrescriptionPriceLabel = getRetailOrPrescriptionPriceLabel({
    currentPrescriptionType,
    isAtSaeNowOffer,
    isOtc,
    superScripts,
  })

  return (
    <div
      className={clsx(
        'mx-5 flex pt-5',
        isRebrushStyle && 'pb-1.5',
        !isRebrushStyle && 'pb-2.5',
      )}
    >
      <div className="mr-9 flex-1 basis-2/4 mobile-md:mr-11 desktop:mr-0 desktop:basis-5/12" />
      <div
        className={clsx(
          'flex flex-1 basis-1/4 justify-end whitespace-nowrap px-2 text-s font-medium text-dark-primary-strong desktop:hidden',
          isErxWithRecommendedPrice && 'flex-col text-right',
          isAtSaeNowOffer && 'flex-col items-end',
        )}
      >
        <span>{retailOrPrescriptionPriceLabel}</span>
        <ProductPriceTransparencyIcon className="ml-1" />
      </div>
      <div
        className={clsx(
          'hidden justify-end px-2 text-s font-medium text-dark-brand desktop:flex',
          isAtSaeNowOffer ? 'basis-4/12 text-right' : 'basis-6/12',
        )}
      >
        <span>{retailOrPrescriptionPriceLabel}</span>
      </div>
      <div
        className={clsx(
          'ml-2.5 flex flex-1 justify-end text-s font-medium desktop:ml-0 desktop:basis-1/12',
          isErxWithRecommendedPrice && 'flex-col justify-end text-right',
          isAtSaeNowOffer ? 'text-right' : 'whitespace-nowrap',
        )}
      >
        {isOtc ? (
          <span className="text-dark-primary-max">
            <FormattedMessage id="product.details.variants.savings.label" />
            <TosSuperscript
              value={
                showThirtyDayPrice &&
                thirtyDayPriceTrend ===
                  PRODUCT_THIRTY_DAYS_PRICE_TREND.PRICE_DOWN
                  ? superScripts.discountThirtyDays
                  : superScripts.saving
              }
            />
          </span>
        ) : null}
      </div>
    </div>
  )
}
